import React, { useState, useEffect } from 'react';
import { appAdminReviewService, appDeveloperService } from '../../lib/services/appstore/index.js';
import { useTranslation } from '../../lib/i18n';
import LanguageSwitcher from '../Common/LanguageSwitcher';
import './AdminReviewSystem.css';

const AdminReviewSystem = ({ currentTenant, onBack, onTabChange }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingReviews, setPendingReviews] = useState([]);
  const [reviewedApps, setReviewedApps] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [reviewForm, setReviewForm] = useState({
    status: '',
    admin_notes: '',
    review_date: new Date().toISOString().split('T')[0]
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    total: 0
  });

  useEffect(() => {
    loadData();
  }, [currentTenant, activeTab]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      if (activeTab === 'pending') {
        const pending = await appAdminReviewService.getPendingReviews(currentTenant);
        setPendingReviews(pending.data || []);
      } else {
        const reviewed = await appAdminReviewService.getReviewStatistics(currentTenant);
        setReviewedApps(reviewed.data || []);
      }
      
      // Load overall statistics
      const overallStats = await appAdminReviewService.getReviewStatistics(currentTenant);
      if (overallStats.data) {
        const statsData = overallStats.data;
        setStats({
          pending: statsData.pending_count || 0,
          approved: statsData.approved_count || 0,
          rejected: statsData.rejected_count || 0,
          total: statsData.total_count || 0
        });
      }
    } catch (err) {
      setError(err.message);
      console.error('Error loading review data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAppSelect = (app) => {
    setSelectedApp(app);
    setReviewForm({
      status: '',
      admin_notes: '',
      review_date: new Date().toISOString().split('T')[0]
    });
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!selectedApp || !reviewForm.status) {
      setError('Please select an app and provide a review status');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let result;
      const reviewData = {
        app_id: selectedApp.id,
        admin_notes: reviewForm.admin_notes,
        review_date: reviewForm.review_date,
        tenant_id: currentTenant
      };

      switch (reviewForm.status) {
        case 'approved':
          result = await appAdminReviewService.approveApp(selectedApp.id, currentTenant, reviewData);
          break;
        case 'rejected':
          result = await appAdminReviewService.rejectApp(selectedApp.id, currentTenant, reviewData);
          break;
        case 'revision':
          result = await appAdminReviewService.requestRevision(selectedApp.id, currentTenant, reviewData);
          break;
        default:
          throw new Error('Invalid review status');
      }

      if (result.success) {
        setSelectedApp(null);
        setReviewForm({
          status: '',
          admin_notes: '',
          review_date: new Date().toISOString().split('T')[0]
        });
        await loadData(); // Refresh data
      }
    } catch (err) {
      setError(err.message);
      console.error('Error submitting review:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAction = async (action, appIds) => {
    if (!appIds || appIds.length === 0) {
      setError('Please select apps to perform bulk action');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const promises = appIds.map(appId => {
        const reviewData = {
          app_id: appId,
          admin_notes: `Bulk ${action} action`,
          review_date: new Date().toISOString().split('T')[0],
          tenant_id: currentTenant
        };

        switch (action) {
          case 'approve':
            return appAdminReviewService.approveApp(appId, currentTenant, reviewData);
          case 'reject':
            return appAdminReviewService.rejectApp(appId, currentTenant, reviewData);
          default:
            return Promise.resolve();
        }
      });

      await Promise.all(promises);
      await loadData(); // Refresh data
    } catch (err) {
      setError(err.message);
      console.error('Error performing bulk action:', err);
    } finally {
      setLoading(false);
    }
  };

  const renderAppCard = (app, isPending = false) => (
    <div key={app.id} className="app-card">
      <div className="app-header">
        <h3>{app.name}</h3>
        <span className={`status ${app.status || 'pending'}`}>
          {app.status || 'Pending Review'}
        </span>
      </div>
      
      <div className="app-details">
        <p><strong>Developer:</strong> {app.developer_name || 'Unknown'}</p>
        <p><strong>Category:</strong> {app.category}</p>
        <p><strong>Description:</strong> {app.description?.substring(0, 100)}...</p>
        <p><strong>Submitted:</strong> {new Date(app.created_at).toLocaleDateString()}</p>
        {app.admin_notes && (
          <p><strong>Admin Notes:</strong> {app.admin_notes}</p>
        )}
      </div>

      <div className="app-actions">
        <button 
          onClick={() => handleAppSelect(app)}
          className="btn btn-primary"
        >
          Review
        </button>
        {isPending && (
          <button 
            onClick={() => handleBulkAction('approve', [app.id])}
            className="btn btn-success"
          >
            Quick Approve
          </button>
        )}
      </div>
    </div>
  );

  const renderReviewForm = () => (
    <div className="review-form">
      <h3>Review App: {selectedApp?.name}</h3>
      
      <form onSubmit={handleReviewSubmit}>
        <div className="form-group">
          <label>Review Status:</label>
          <select
            value={reviewForm.status}
            onChange={(e) => setReviewForm({...reviewForm, status: e.target.value})}
            required
          >
            <option value="">Select Status</option>
            <option value="approved">Approve</option>
            <option value="rejected">Reject</option>
            <option value="revision">Request Revision</option>
          </select>
        </div>

        <div className="form-group">
          <label>Admin Notes:</label>
          <textarea
            value={reviewForm.admin_notes}
            onChange={(e) => setReviewForm({...reviewForm, admin_notes: e.target.value})}
            placeholder="Provide detailed feedback..."
            rows="4"
          />
        </div>

        <div className="form-group">
          <label>Review Date:</label>
          <input
            type="date"
            value={reviewForm.review_date}
            onChange={(e) => setReviewForm({...reviewForm, review_date: e.target.value})}
            required
          />
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Submitting...' : 'Submit Review'}
          </button>
          <button 
            type="button" 
            onClick={() => setSelectedApp(null)}
            className="btn btn-secondary"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );

  return (
    <div className="admin-review-system">
      <div className="header">
        <button onClick={onBack} className="back-btn">
          ← {t('common.back')}
        </button>
        <h1>{t('adminReviewSystem.title')}</h1>
        <LanguageSwitcher />
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="stats-overview">
        <div className="stat-card">
          <h3>Pending</h3>
          <span className="stat-number">{stats.pending}</span>
        </div>
        <div className="stat-card">
          <h3>Approved</h3>
          <span className="stat-number approved">{stats.approved}</span>
        </div>
        <div className="stat-card">
          <h3>Rejected</h3>
          <span className="stat-number rejected">{stats.rejected}</span>
        </div>
        <div className="stat-card">
          <h3>Total</h3>
          <span className="stat-number">{stats.total}</span>
        </div>
      </div>

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          Pending Reviews ({stats.pending})
        </button>
        <button
          className={`tab ${activeTab === 'reviewed' ? 'active' : ''}`}
          onClick={() => setActiveTab('reviewed')}
        >
          Reviewed Apps
        </button>
      </div>

      <div className="content">
        {loading ? (
          <div className="loading">Loading...</div>
        ) : selectedApp ? (
          renderReviewForm()
        ) : (
          <div className="apps-list">
            {activeTab === 'pending' ? (
              <>
                {pendingReviews.length > 0 && (
                  <div className="bulk-actions">
                    <button 
                      onClick={() => handleBulkAction('approve', pendingReviews.map(app => app.id))}
                      className="btn btn-success"
                    >
                      Bulk Approve All
                    </button>
                  </div>
                )}
                {pendingReviews.length === 0 ? (
                  <div className="empty-state">
                    <p>No pending reviews</p>
                  </div>
                ) : (
                  pendingReviews.map(app => renderAppCard(app, true))
                )}
              </>
            ) : (
              <>
                {reviewedApps.length === 0 ? (
                  <div className="empty-state">
                    <p>No reviewed apps</p>
                  </div>
                ) : (
                  reviewedApps.map(app => renderAppCard(app, false))
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReviewSystem;
